import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { MyAssignment } from '../../../../../api/assignment';
import MyAssignmentsContext from '../../../../../contexts/MyAssignmentsContext';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import ModuleContext from '../../../_contexts/ModuleContext';
import { getFinishedStudentAssignments, type FinishedStudentAssignment } from '../api/finished-student-assignment';

export type StudentAssignment = MyAssignment | FinishedStudentAssignment;

export default function useStudentAssignments(finished?: boolean) {
  const [assignments, setAssignments] = useState<StudentAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useThrowAsyncError();
  const { module, allModules } = useContext(ModuleContext);
  const {
    assignments: _assignments,
    isLoading: _isLoading,
    refreshAssignments: refetchMyAssignments,
  } = useContext(MyAssignmentsContext);

  const allModuleIds = useMemo(() => allModules.map(m => m.id), [allModules]);

  const filterOrFetchFinishedAssignment = useCallback(async () => {
    if (!module) return;

    if (!finished) {
      const assignmentsByModule = _assignments.filter(a => allModuleIds.includes(a.moduleId));
      setAssignments(assignmentsByModule);
      setIsLoading(_isLoading);
      return;
    }

    setIsLoading(true);

    try {
      const fetchedAssignments = await getFinishedStudentAssignments(module.id);
      setAssignments(fetchedAssignments);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [module, finished, _assignments, _isLoading, allModuleIds, handleError]);

  useEffect(() => {
    filterOrFetchFinishedAssignment();
  }, [filterOrFetchFinishedAssignment]);

  const refetchAssignments = useCallback(async () => {
    if (!module) return;

    await refetchMyAssignments();
  }, [module, refetchMyAssignments]);

  return { assignments, fetchAssignments: refetchAssignments, isLoading };
}
